import { Head, router, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

import CaseForm from "./_form"
import type { EmployeeAccessData } from "./_form"

interface Option {
  id: number
  name: string
}

interface StatusOption {
  value: number
  label: string
}

interface CaseRecord {
  case_number: string | null
  file_number: string
  case_reference: string
  hpi_name: string
  qb_name: string
  report_name: string
  directory: string
  customer_id: number | null
  customer_user_id: number | null
  customer_location_id: number | null
  investigation_type_id: number | null
  manager_id: number | null
  referral_case_id: number | null
  location_city: string
  location_state: string
  open_date: string | null
  closed_date: string | null
  accepted_date: string | null
  paid_date: string | null
  budget: number | null
  retainer: number | null
  results: number | null
  results_notes: string
  notes: string
  objectives: string
  internal_notes: string
  has_attorney: boolean
  attorney_name: string
  attorney_phone: string
  attorney_law_firm: string
  attorney_email: string
  prepared_for: string
  matter: string
  accepted: number | null
  accepted_text: string
  after_hours: boolean
  is_public: boolean
  hpi_company: number | null
  status: number
  intake_by_id: number | null
  other_type: string
  insurance_days: number | null
  insurance_budget_notes: string
  referral_customer_id: number | null
  referral_customer_user_id: number | null
}

interface EmployeeCheckbox {
  id: number
  name: string
  checked: boolean
}

interface NotifyEmployee {
  id: number
  name: string
  forced: boolean
  default_checked: boolean
}

interface Props {
  case_record: CaseRecord
  customer_display_name?: string
  investigation_types: Option[]
  managers: Option[]
  users: Option[]
  status_options: StatusOption[]
  referral_display_name?: string
  errors?: Record<string, string[]>
  allow_employees?: EmployeeCheckbox[]
  deny_employees?: EmployeeCheckbox[]
  notify_employees?: NotifyEmployee[]
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Cases", href: "/cases" },
  { title: "New Case", href: "/cases/new" },
]

export default function New({
  case_record,
  customer_display_name = "",
  investigation_types,
  managers,
  users,
  status_options,
  referral_display_name = "",
  errors,
  allow_employees = [],
  deny_employees = [],
  notify_employees = [],
}: Props) {
  const { data, setData, processing } = useForm<CaseRecord>(case_record)

  const handleSubmit = (
    e: React.FormEvent,
    employeeData: EmployeeAccessData,
  ) => {
    e.preventDefault()
    router.post("/cases", { ...data, ...employeeData })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="New Case" />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">New Case</h1>

        <CaseForm
          data={data}
          setData={setData}
          errors={errors}
          processing={processing}
          onSubmit={handleSubmit}
          submitLabel="Create Case"
          customer_display_name={customer_display_name}
          investigation_types={investigation_types}
          managers={managers}
          users={users}
          status_options={status_options}
          referral_display_name={referral_display_name}
          isEdit={false}
          allow_employees={allow_employees}
          deny_employees={deny_employees}
          notify_employees={notify_employees}
        />
      </div>
    </AppLayout>
  )
}
